import { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { Home, Shield, ChevronDown, Check } from 'lucide-react';

const LABEL = { resident: 'Resident', trustee: 'Trustee' };
const ICON = { resident: Home, trustee: Shield };
const DEST = { resident: '/dashboard', trustee: '/admin' };
const STORAGE_KEY = 'active_role_view';

export function useActiveRole() {
  const { user } = useAuth();
  const [activeRole, setActiveRole] = useState(() => {
    try { return localStorage.getItem(STORAGE_KEY) || null; } catch { return null; }
  });

  // Keep in sync: if user has no dual role, always mirror their primary role.
  useEffect(() => {
    if (!user) return;
    const primary = user.role;
    const secondary = user.secondary_roles || [];
    const options = [primary, ...secondary];
    const isValid = activeRole && options.includes(activeRole);
    if (!isValid) {
      setActiveRole(primary);
      try { localStorage.setItem(STORAGE_KEY, primary); } catch (_) {}
    }
  }, [user, activeRole]);

  const switchTo = (role) => {
    setActiveRole(role);
    try { localStorage.setItem(STORAGE_KEY, role); } catch (_) {}
  };

  return { activeRole, switchTo };
}

/**
 * Dual-role switcher. Renders only if the user has at least one secondary role
 * (i.e. a Resident who is also a Trustee, or vice versa). Otherwise returns null.
 */
export default function RoleSwitcher() {
  const { user } = useAuth();
  const { activeRole, switchTo } = useActiveRole();
  const navigate = useNavigate();
  const [open, setOpen] = useState(false);

  if (!user) return null;
  const secondary = user.secondary_roles || [];
  // Only Resident/Trustee are supported in the switcher for now.
  const relevantSecondary = secondary.filter((r) => r === 'resident' || r === 'trustee');
  if (relevantSecondary.length === 0) return null;

  const options = [];
  if (user.role === 'resident' || user.role === 'trustee') options.push(user.role);
  relevantSecondary.forEach((r) => { if (!options.includes(r)) options.push(r); });
  if (options.length < 2) return null;

  const current = activeRole || user.role;
  const CurrentIcon = ICON[current] || Home;

  const handleSwitch = (r) => {
    switchTo(r);
    setOpen(false);
    navigate(DEST[r] || '/dashboard');
  };

  return (
    <div className="relative inline-block text-left" data-testid="role-switcher">
      <button
        type="button"
        onClick={() => setOpen((o) => !o)}
        className="flex items-center gap-2 px-3 py-1.5 rounded-md border bg-white hover:bg-gray-50 text-sm"
        data-testid="role-switcher-trigger"
      >
        <CurrentIcon className="w-4 h-4 text-indigo-600" />
        <span className="font-medium">{LABEL[current] || current}</span>
        <span className="text-[10px] uppercase tracking-wide text-slate-400 hidden sm:inline">view</span>
        <ChevronDown className="w-4 h-4 text-gray-400" />
      </button>

      {open && (
        <>
          <div className="fixed inset-0 z-20" onClick={() => setOpen(false)} />
          <div className="absolute right-0 mt-1 w-56 bg-white border rounded-lg shadow-lg z-30" data-testid="role-switcher-menu">
            <div className="px-3 py-2 text-xs text-gray-500 uppercase tracking-wide border-b">
              Switch view
            </div>
            {options.map((r) => {
              const Icon = ICON[r] || Home;
              const isActive = r === current;
              return (
                <button
                  key={r}
                  type="button"
                  onClick={() => handleSwitch(r)}
                  className={`w-full flex items-center gap-2 px-3 py-2.5 text-left text-sm hover:bg-indigo-50 border-b last:border-0 ${isActive ? 'bg-indigo-50/60' : ''}`}
                  data-testid={`role-switcher-option-${r}`}
                >
                  <Icon className={`w-4 h-4 ${isActive ? 'text-indigo-600' : 'text-slate-500'}`} />
                  <span className="flex-1 font-medium">{LABEL[r]}</span>
                  {isActive && <Check className="w-4 h-4 text-indigo-600" />}
                </button>
              );
            })}
          </div>
        </>
      )}
    </div>
  );
}
